import React, { useEffect, useState, useRef, useCallback } from "react";
import LoadingComponent from "../../utils/loadingComponent";
import classnames from "classnames";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Profiletabs } from 'constants/patient';
import { encodePatientId } from "helpers/url_crypto_helper";
import {
  RiUserLine,
  RiSearchEyeLine,
  RiMentalHealthLine,
  RiHeartPulseLine,
  RiMedicineBottleLine,
  RiFolder2Line,
  RiFileTextLine,
  RiMoneyDollarCircleLine,
  RiBook2Line,
  RiCalendarEventLine,
  RiFileEditLine,
  RiNotification3Line,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";

const Components = {
    Profile: React.lazy(() => import("./tabs/profile")),
    Investigations: React.lazy(() => import("./tabs/investigations")),
    Examination: React.lazy(() => import("./tabs/examination")),
    Treatment: React.lazy(() => import("./tabs/treatment")),
    Prescription: React.lazy(() => import("./tabs/prescription")),
    Files: React.lazy(() => import("./tabs/files")),
    Bills: React.lazy(() => import("./tabs/billing")),
    Payments: React.lazy(() => import("./tabs/payments")),
    Ledger: React.lazy(() => import("./tabs/ledger")),
    Appointments: React.lazy(() => import("./tabs/appointments")),
    Notes: React.lazy(() => import("./tabs/notes")),
    Followup: React.lazy(() => import("./tabs/followUp"))
};

const TabIcons = {
    Profile: <RiUserLine />,
    Investigations: <RiSearchEyeLine />,
    Examination: <RiMentalHealthLine />,
    Treatment: <RiHeartPulseLine />,
    Prescription: <RiMedicineBottleLine />,
    Files: <RiFolder2Line />,
    Bills: <RiFileTextLine />,
    Payments: <RiMoneyDollarCircleLine />,
    Ledger: <RiBook2Line />,
    Appointments: <RiCalendarEventLine />,
    Notes: <RiFileEditLine />,
    Followup: <RiNotification3Line />
};

const TabLabels = {
    Profile: "Demographics",
    Investigations: "Investigations",
    Examination: "Examinations",
    Treatment: "Treatment Plan",
    Prescription: "Prescriptions",
    Files: "Files & X-Rays",
    Bills: "Bills & Invoices",
    Payments: "Payments",
    Ledger: "Account Ledger",
    Appointments: "Appointments",
    Notes: "Clinical Notes",
    Followup: "Follow-Up"
};

import { isModuleEnabled, isPaymentModuleEnabled, isDentalChartEnabled, isPrescriptionsEnabled, isInvestigationsEnabled, isNotesEnabled } from "helpers/tenantModuleHelper";

const Tabs = ({ patientData, configData = null, role = 'admin' }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const queryParams = new URLSearchParams(location.search);
    const tabName = queryParams.get('_t');
    const [formAutoOpen, setFormAutoOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(tabName || "Profile");

    const scrollContainerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    let tabs = Profiletabs[role] || Profiletabs['admin'];

    // Dynamic Module Feature Gating
    const excludedTabs = [];
    if (!isPaymentModuleEnabled() || (configData?.user?.config && configData?.user?.config?.billing_info === false)) {
        excludedTabs.push("Bills", "Payments", "Ledger");
    }
    if (!isDentalChartEnabled()) {
        excludedTabs.push("Examination");
    }
    if (!isPrescriptionsEnabled()) {
        excludedTabs.push("Prescription");
    }
    if (!isInvestigationsEnabled()) {
        excludedTabs.push("Investigations", "Files");
    }
    if (!isNotesEnabled()) {
        excludedTabs.push("Notes");
    }

    if (excludedTabs.length > 0) {
        tabs = tabs.filter(t => !excludedTabs.includes(t));
    }

    const checkScrollButtons = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 5);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }, []);

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -240, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 240, behavior: 'smooth' });
        }
    };

    const handleWheel = (e) => {
        if (scrollContainerRef.current) {
            if (e.deltaY !== 0) {
                scrollContainerRef.current.scrollLeft += e.deltaY;
            }
        }
    };

    const handleTabChange = (selectedTab) => {
        setActiveTab(selectedTab);
        queryParams.set('_t', selectedTab);
        history.push({
            pathname: location.pathname,
            search: `?${queryParams.toString()}`
        });
    };

    useEffect(() => {
        if (tabName && tabs.includes(tabName)) {
            setActiveTab(tabName);
        } else if (!tabName && tabs.length > 0) {
            setActiveTab(tabs[0]);
        }
    }, [tabName]);

    // Check scroll capability on mount and on resize
    useEffect(() => {
        checkScrollButtons();
        const el = scrollContainerRef.current;
        if (el) {
            el.addEventListener('scroll', checkScrollButtons);
            window.addEventListener('resize', checkScrollButtons);
        }
        return () => {
            if (el) el.removeEventListener('scroll', checkScrollButtons);
            window.removeEventListener('resize', checkScrollButtons);
        };
    }, [tabs, checkScrollButtons]);

    // Auto-scroll active tab into view
    useEffect(() => {
        if (scrollContainerRef.current) {
            const activeBtn = scrollContainerRef.current.querySelector('.emr-nav-link.active');
            if (activeBtn) {
                activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
        setTimeout(checkScrollButtons, 350);
    }, [activeTab, checkScrollButtons]);

    const ActiveComponent = Components[activeTab] || Components["Profile"];

    return (
        <div className="patient-emr-tabs-section">
            {/* Horizontal Tab Navigation Ribbon with Left/Right Navigation Arrows */}
            <div className="emr-tabs-nav-card position-relative">
                {canScrollLeft && (
                    <button
                        type="button"
                        className="emr-tab-scroll-btn emr-tab-scroll-left"
                        onClick={scrollLeft}
                        aria-label="Scroll Tabs Left"
                    >
                        <RiArrowLeftSLine />
                    </button>
                )}

                <ul
                    ref={scrollContainerRef}
                    className="emr-nav-pills list-unstyled mb-0"
                    onWheel={handleWheel}
                >
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab;
                        const labelKey = TabLabels[tab] || tab;
                        return (
                            <li key={tab} className="emr-nav-item">
                                <button
                                    type="button"
                                    className={`emr-nav-link ${isActive ? 'active' : ''}`}
                                    onClick={() => handleTabChange(tab)}
                                >
                                    {TabIcons[tab] || <RiFileTextLine />}
                                    <span>{t(labelKey) || labelKey}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {canScrollRight && (
                    <button
                        type="button"
                        className="emr-tab-scroll-btn emr-tab-scroll-right"
                        onClick={scrollRight}
                        aria-label="Scroll Tabs Right"
                    >
                        <RiArrowRightSLine />
                    </button>
                )}
            </div>

            {/* Active Tab Viewport */}
            <div className="emr-tab-content-wrapper">
                <React.Suspense fallback={<LoadingComponent />}>
                    {ActiveComponent ? (
                        <ActiveComponent isFormPreOpen={false} patientData={patientData} />
                    ) : (
                        <LoadingComponent />
                    )}
                </React.Suspense>
            </div>
        </div>
    );
};

export default Tabs;